import { motion } from 'framer-motion';
import { LucideIcon, Plus, Lock, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
} from "@/components/ui/tooltip";
import { ReactNode } from 'react';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08
        }
    }
};

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.25,
            ease: "easeOut"
        }
    }
};

interface EmptyStateAction {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
    disabled?: boolean;
    disabledReason?: string;
    size?: "default" | "sm" | "lg";
    variant?: "default" | "outline" | "secondary" | "ghost";
}

interface EmptyStateProps {
    /** Icon to display */
    icon: LucideIcon;
    
    /** Main title */
    title: string;
    
    /** Primary description */
    description: string;
    
    /** Optional secondary description */
    secondaryDescription?: string;
    
    /** Primary action button - can be an action object or a custom React node */
    action?: EmptyStateAction | ReactNode;
    
    /** Optional info card content */
    infoCard?: {
        title?: string;
        content: string | ReactNode;
    };
    
    /** Custom className for the container */
    className?: string;
    
    /** Minimum height */
    minHeight?: string;
}

export const EmptyState = ({
    icon: Icon,
    title,
    description,
    secondaryDescription,
    action,
    infoCard,
    className = "",
    minHeight = "min-h-[400px]"
}: EmptyStateProps) => {
    // Check if action is a ReactNode or EmptyStateAction object
    const isActionObject = action && typeof action === 'object' && 'label' in action && 'onClick' in action;
    
    const ActionButton = action ? (
        isActionObject ? (() => {
            const actionObj = action as EmptyStateAction;
            const ActionIcon = actionObj.icon || Plus;
            return (
                <Button
                    onClick={actionObj.onClick}
                    disabled={actionObj.disabled}
                    size={actionObj.size || "default"}
                    variant={actionObj.variant || "default"}
                    className="gap-2"
                >
                    <ActionIcon className="h-4 w-4" />
                    {actionObj.label}
                    {actionObj.disabled && <Lock className="w-3.5 h-3.5 ml-1 opacity-70" />}
                </Button>
            );
        })() : (
            action as ReactNode
        )
    ) : null;

    return (
        <motion.div
            className={`flex flex-col items-center justify-center ${minHeight} w-full px-4 py-8 ${className}`}
            initial="hidden"
            animate="visible"
            variants={containerVariants}
        >
            {/* Icon Container */}
            <motion.div variants={itemVariants} className="relative mb-6">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20 blur-md" />
                <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-2 ring-primary/20 shadow-lg">
                    <Icon className="h-8 w-8 text-primary" />
                </div>
            </motion.div>

            {/* Text Content */}
            <motion.div variants={itemVariants} className="text-center space-y-3 max-w-md">
                <h2 className="text-2xl font-semibold tracking-tight">
                    {title}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                    {description}
                </p>
                {secondaryDescription && (
                    <p className="text-sm text-muted-foreground/80 leading-relaxed">
                        {secondaryDescription}
                    </p>
                )}
            </motion.div>

            {/* Action Button */}
            {action && (
                <motion.div variants={itemVariants} className="mt-6">
                    {isActionObject && (action as EmptyStateAction).disabled && (action as EmptyStateAction).disabledReason ? (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        {ActionButton}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" sideOffset={5} className="flex items-center gap-2 max-w-xs">
                                    <ShieldAlert className="w-12 h-12 text-amber-500 shrink-0" />
                                    <p className="text-sm">{(action as EmptyStateAction).disabledReason}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        ActionButton
                    )}
                </motion.div>
            )}

            {/* Info Card */}
            {infoCard && (
                <motion.div
                    variants={itemVariants}
                    className="w-full max-w-md mt-8 p-4 rounded-lg
                        bg-muted/50 
                        border border-border
                        backdrop-blur-sm"
                >
                    {infoCard.title && (
                        <h3 className="text-sm font-medium mb-2">{infoCard.title}</h3>
                    )}
                    {typeof infoCard.content === 'string' ? (
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            {infoCard.content}
                        </p>
                    ) : (
                        infoCard.content
                    )}
                </motion.div>
            )}
        </motion.div>
    );
};

